import { JUDGE_POSITIONS } from "score-common";

// #2365 (scribe-presence workstream, card D). Trello card #2365, comment
// from Jaybill McCarthy, 2026-09-07: GET /presence (score-api, #2363) only
// ever returns a raw lastSeenAt, never an isOnline verdict -- deciding a
// "quiet too long" threshold is explicitly a DISPLAY decision each consumer
// makes for itself, and each consumer is EXPECTED to pick a different
// number (announcer-fe/#2366 is a call-the-next-rider decision and needs a
// tighter one). Scoreadmin is a whole-show glance, so this one is
// deliberately loose: 2.5 minutes of silence. Starting value, not a
// researched figure -- confirm in practice, per the card.
export const SCRIBE_QUIET_THRESHOLD_MS = 150000;

export function presenceKey({ ring, seat, classId }) {
  return `${ring ?? ""}#${seat ?? ""}#${classId ?? ""}`;
}

// Folds a fresh GET /presence response into the running "what has this
// dashboard personally observed" map (#2365). GET /presence hard-drops any
// row whose heartbeat is more than PRESENCE_TTL_SECONDS old (score-api's
// lib/presence.js, isPresenceLive) -- once a row ages out, the ENDPOINT
// itself can no longer tell "just went quiet" apart from "never connected"
// (card's contract warning: "you cannot, from this endpoint"). This map is
// how scoreadmin answers that WITHOUT pretending the endpoint told it
// something it didn't: as long as scoreadmin keeps polling, it remembers the
// last entry IT personally saw at each (ring, seat, classId) key and keeps
// that entry's lastSeenAt on screen -- ticking up in real, unmodified time,
// never invented -- even after a later poll stops returning it. A key this
// dashboard has never once observed is never added here at all, so it
// renders as "not connected" with no elapsed time, never as a fabricated
// long silence.
export function mergePresenceObservations(existingByKey, freshEntries) {
  if (!freshEntries || freshEntries.length === 0) return existingByKey;
  const next = { ...existingByKey };
  for (const entry of freshEntries) {
    if (!entry || !entry.ring || !entry.seat) continue;
    next[presenceKey(entry)] = entry;
  }
  return next;
}

// The judged positions a class actually stages, in JUDGE_POSITIONS order --
// the same rule ClassManager.jsx uses to decide which seats are worth a
// scribe picker (FVD's judge panel, #555): only positions with at least one
// real judgeAssignments entry. This is "staffed on the daysheet" (AC2) --
// independent of whether scoreadmin's OWN scribeAssignments tool has anyone
// slotted in, since that's a different (internal) fact from what FVD's own
// panel schedules.
export function positionsForClass(classItem) {
  if (!classItem) return [];
  return JUDGE_POSITIONS.filter(
    (p) => Array.isArray(classItem.judgeAssignments?.[p]) && classItem.judgeAssignments[p].length
  );
}

function buildRow({ ring, seat, classId, observed, now, quietThresholdMs, unexpected }) {
  const lastSeenAt = observed?.lastSeenAt ?? null;
  const elapsedMs = lastSeenAt != null ? Math.max(0, now - new Date(lastSeenAt).getTime()) : null;
  return {
    ring,
    seat,
    // Carried through so the render key can be presenceKey(row) (same
    // (ring, seat, classId) identity usePresence's observation map already
    // keys on) rather than a bare `${ring}-${seat}` -- a ring with no
    // current class can surface two "unexpected" observations at the same
    // (ring, seat) under two different classIds (e.g. a stale class-A
    // heartbeat still inside its TTL alongside a fresh class-B one), which
    // collided as duplicate React keys before this field existed.
    classId,
    scribeName: observed?.scribeName ?? null,
    lastSeenAt,
    elapsedMs,
    everConnected: lastSeenAt != null,
    quiet: elapsedMs != null && elapsedMs >= quietThresholdMs,
    unexpected,
  };
}

// Builds the flat (ring, seat) row list the table renders (AC4): one row per
// staffed seat in every ring currently holding a class, in ring/seat order
// (AC1/AC2), PLUS any live presence entry claiming a ring/seat that ring's
// current class panel doesn't stage -- surfaced, never dropped, per #2363's
// "claim, not authorization fact" contract note.
export function buildPresenceRows({
  ringRows,
  observedByKey,
  now,
  quietThresholdMs = SCRIBE_QUIET_THRESHOLD_MS,
}) {
  const rows = [];
  const seenKeys = new Set();

  for (const ringRow of ringRows || []) {
    const { ring, class: currentClass } = ringRow;
    const classId = currentClass?.id ?? null;
    const expectedSeats = positionsForClass(currentClass);

    for (const seat of expectedSeats) {
      const key = presenceKey({ ring, seat, classId });
      seenKeys.add(key);
      const observed = observedByKey[key] || null;
      rows.push(buildRow({ ring, seat, classId, observed, now, quietThresholdMs, unexpected: false }));
    }

    // Any observation reported for THIS ring -- for this ring's CURRENT
    // class when there is one, or at all when the ring has no known current
    // class -- at a seat the expected set above didn't already cover.
    for (const [key, observed] of Object.entries(observedByKey || {})) {
      if (seenKeys.has(key)) continue;
      if (observed.ring !== ring) continue;
      if (currentClass && (observed.classId ?? null) !== classId) continue;
      seenKeys.add(key);
      rows.push(
        buildRow({
          ring,
          seat: observed.seat,
          classId: observed.classId ?? null,
          observed,
          now,
          quietThresholdMs,
          unexpected: true,
        })
      );
    }
  }

  return rows;
}

// "45s" / "3m 12s" / "12m" -- a plain elapsed-time label. Only the i18n KEY
// wrapping this value is a static literal (CLAUDE.md: no dynamic i18n
// keys); this string itself is an interpolated value, not a translated key.
export function formatElapsedLabel(ms) {
  if (ms == null) return null;
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  if (seconds === 0) return `${minutes}m`;
  return `${minutes}m ${seconds}s`;
}
